import { z } from 'zod'

import { COUNTRIES } from './countries.data'

/**
 * Generic address schema that can be used across different modules.
 *
 * `country` holds an ISO 3166-1 alpha-2 code for values written by the
 * ADDRESS form/custom field types. Older values (entity, supplier, billing
 * profile) may carry a free-text country name; readers resolve either with
 * `resolveCountryCode` / `getCountryName`.
 */
export const AddressSchema = z.object({
  street: z.string().optional(),
  street2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  formatted: z.string().optional(),
})

/**
 * Type definition for the address schema
 */
export type Address = z.infer<typeof AddressSchema>

// =====================
// Parts
// =====================

/** Editable parts of a structured address, in display order. */
export const ADDRESS_PARTS = [
  'street',
  'street2',
  'city',
  'state',
  'postalCode',
  'country',
] as const
export type AddressPart = (typeof ADDRESS_PARTS)[number]
export const AddressPartEnum = z.enum(ADDRESS_PARTS)

export const ADDRESS_PART_LABELS: Record<AddressPart, string> = {
  street: 'Street address',
  street2: 'Apartment, suite, etc.',
  city: 'City',
  state: 'State / region',
  postalCode: 'Postal code',
  country: 'Country',
}

/**
 * HTML `autocomplete` tokens per part so browser / OS autofill can fill the
 * whole address from a saved profile.
 */
export const ADDRESS_PART_AUTOCOMPLETE: Record<AddressPart, string> = {
  street: 'address-line1',
  street2: 'address-line2',
  city: 'address-level2',
  state: 'address-level1',
  postalCode: 'postal-code',
  country: 'country',
}

/** Upper bound for any single address part. */
export const ADDRESS_PART_MAX_LENGTH = 255

// =====================
// Countries
// =====================

export { COUNTRIES }

const COUNTRY_NAME_BY_CODE = new Map(COUNTRIES.map(c => [c.code, c.name]))

// Common aliases an importer or a legacy value may carry.
const COUNTRY_ALIASES: Record<string, string> = {
  usa: 'US',
  'u.s.': 'US',
  'u.s.a.': 'US',
  'united states of america': 'US',
  america: 'US',
  uk: 'GB',
  'u.k.': 'GB',
  'great britain': 'GB',
  britain: 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'northern ireland': 'GB',
  holland: 'NL',
  'the netherlands': 'NL',
  deutschland: 'DE',
  turkey: 'TR',
  'czech republic': 'CZ',
  'south korea': 'KR',
  korea: 'KR',
  'ivory coast': 'CI',
  "cote d'ivoire": 'CI',
  uae: 'AE',
}

const COUNTRY_CODE_BY_NAME = new Map<string, string>([
  ...COUNTRIES.map(c => [c.name.toLowerCase(), c.code] as [string, string]),
  ...Object.entries(COUNTRY_ALIASES),
])

export function isValidCountryCode(code: unknown): code is string {
  return typeof code === 'string' && COUNTRY_NAME_BY_CODE.has(code)
}

/**
 * Resolve a country code or name (any case, common aliases) to its ISO
 * alpha-2 code. Returns null when it can't be resolved.
 */
export function resolveCountryCode(input: unknown): string | null {
  if (typeof input !== 'string') return null
  const trimmed = input.trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  if (upper.length === 2 && COUNTRY_NAME_BY_CODE.has(upper)) return upper
  return COUNTRY_CODE_BY_NAME.get(trimmed.toLowerCase()) ?? null
}

/**
 * Display name for a stored country value. Codes resolve to their name;
 * legacy free-text values are returned unchanged.
 */
export function getCountryName(value: string | null | undefined): string {
  if (!value) return ''
  const code = value.trim().toUpperCase()
  return COUNTRY_NAME_BY_CODE.get(code) ?? value
}

// =====================
// Postal codes
// =====================

/**
 * Deliberately simple per-country postal code formats. Countries not listed
 * are accepted as-is (format unchecked).
 */
export const POSTAL_CODE_PATTERNS: Readonly<Record<string, RegExp>> = {
  // ZIP or ZIP+4
  US: /^\d{5}(?:-\d{4})?$/,
  // A1A 1A1 (space optional)
  CA: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
  // Outward + inward code, e.g. SW1A 1AA, M1 1AE, GIR 0AA
  GB: /^(?:GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i,
  AU: /^\d{4}$/,
  DE: /^\d{5}$/,
  FR: /^\d{5}$/,
  // 1234 AB (space optional; SA/SD/SS are not issued)
  NL: /^[1-9]\d{3} ?(?!SA|SD|SS)[A-Z]{2}$/i,
}

export function hasPostalCodePattern(countryCode: string | null | undefined) {
  return !!countryCode && countryCode.toUpperCase() in POSTAL_CODE_PATTERNS
}

/**
 * True when the postal code matches the country's format, or when the
 * country's format isn't checked.
 */
export function isValidPostalCode(
  postalCode: string,
  countryCode: string | null | undefined,
): boolean {
  const pattern = countryCode
    ? POSTAL_CODE_PATTERNS[countryCode.toUpperCase()]
    : undefined
  if (!pattern) return true
  return pattern.test(postalCode.trim())
}

// =====================
// Normalization & formatting
// =====================

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  )
}

/** True for a plain object whose keys are all address keys. */
export function isAddressLike(value: unknown): value is Address {
  if (!isPlainObject(value)) return false
  const keys = Object.keys(value)
  if (keys.length === 0) return false
  const allowed = new Set<string>([...ADDRESS_PARTS, 'formatted', 'zipCode'])
  return keys.every(key => allowed.has(key))
}

/** True when no address part carries text. */
export function isAddressEmpty(value: unknown): boolean {
  if (!isPlainObject(value)) return true
  return ADDRESS_PARTS.every(part => {
    const v = value[part]
    return typeof v !== 'string' || v.trim() === ''
  })
}

/**
 * Normalize an address value for storage: trims parts, drops empty parts,
 * maps a resolvable country name to its code, and accepts a one-line string
 * (best-effort parse). Returns null when nothing usable remains.
 */
export function normalizeAddressValue(value: unknown): Address | null {
  if (typeof value === 'string') {
    return normalizeAddressValue(parseAddressLine(value))
  }
  if (!isPlainObject(value)) return null

  const result: Address = {}
  for (const part of ADDRESS_PARTS) {
    const raw =
      part === 'postalCode' ? (value[part] ?? value.zipCode) : value[part]
    if (typeof raw !== 'string' && typeof raw !== 'number') continue
    const text = String(raw).trim().slice(0, ADDRESS_PART_MAX_LENGTH)
    if (text) result[part] = text
  }
  if (result.country) {
    result.country = resolveCountryCode(result.country) ?? result.country
  }
  return Object.keys(result).length > 0 ? result : null
}

/**
 * Multi-line display: street lines, "City, State Postal", country name.
 */
export function formatAddressLines(address: Address | null | undefined) {
  if (!address) return []
  const cityLine = [
    [address.city, address.state].filter(Boolean).join(', '),
    address.postalCode,
  ]
    .filter(Boolean)
    .join(' ')
  return [
    address.street,
    address.street2,
    cityLine,
    address.country ? getCountryName(address.country) : undefined,
  ].filter((line): line is string => !!line && line.trim() !== '')
}

/** Single-line display for table cells, exports and search. */
export function formatAddressOneLine(
  address: Address | null | undefined,
): string {
  return formatAddressLines(address).join(', ')
}

/**
 * Format an address object into a human-readable string (legacy layout used
 * by invoice documents; ADDRESS fields use formatAddressLines/OneLine).
 */
export function formatAddress(address: Address): string {
  const parts = [
    address.street,
    [address.city, address.state, address.postalCode]
      .filter(Boolean)
      .join(', '),
    address.country,
  ].filter(Boolean)

  return parts.join('\n')
}

/**
 * Parse a formatted (multi-line) address string into an Address object
 */
export function parseAddress(formattedAddress: string): Partial<Address> {
  // Simple parsing logic - can be enhanced based on needs
  const lines = formattedAddress.split('\n').map(line => line.trim())

  if (lines.length === 0) return {}

  // Assume first line is street
  const address: Partial<Address> = {
    street: lines[0],
  }

  // If we have more lines
  if (lines.length > 1) {
    // Last line is typically country
    if (lines.length > 2) {
      address.country = lines[lines.length - 1]
    }

    // Middle line typically contains city, state, postal code
    const cityStateLineIndex = lines.length > 2 ? lines.length - 2 : 1
    const cityStateLine = lines[cityStateLineIndex]

    if (cityStateLine) {
      const parts = cityStateLine.split(',').map(part => part.trim())

      if (parts.length > 0) {
        address.city = parts[0]

        if (parts[1]) {
          // Try to separate state and postal code if they exist
          const statePostal = parts[1].split(' ').map(part => part.trim())
          if (statePostal.length > 0) {
            address.state = statePostal[0]
            if (statePostal.length > 1) {
              address.postalCode = statePostal.slice(1).join(' ')
            }
          }
        }
      }
    }
  }

  return address
}

/**
 * Best-effort parse of a one-line address such as
 * "1 Main St, Springfield, IL 62701, United States" (commas or newlines).
 * The trailing segment becomes the country only when it resolves to one; a
 * segment holding a trailing postal-looking token is split into region +
 * postal code. Anything unrecognized stays in the street.
 */
export function parseAddressLine(text: string): Address {
  const segments = text
    .split(/[\n,]/)
    .map(segment => segment.trim())
    .filter(Boolean)
  if (segments.length === 0) return {}
  if (segments.length === 1) return { street: segments[0] }

  const result: Address = {}
  const last = segments[segments.length - 1]
  const lastCountry = resolveCountryCode(last)
  if (lastCountry) {
    result.country = lastCountry
    segments.pop()
  }

  // Region + postal: "IL 62701", "SW1A 1AA", "NSW 2000", "62701"
  if (segments.length > 1) {
    const regionSegment = segments[segments.length - 1]
    const match = /^(.*?)[\s]*([A-Z]{0,2}\d[\dA-Z -]{1,9})$/i.exec(
      regionSegment,
    )
    if (match && /\d/.test(match[2])) {
      const region = match[1].trim()
      result.postalCode = match[2].trim()
      if (region) result.state = region
      segments.pop()
    }
  }

  if (segments.length > 1) {
    const candidate = segments[segments.length - 1]
    if (!result.state && segments.length > 2) {
      result.state = candidate
      segments.pop()
    }
    result.city = segments.pop()
  }
  result.street = segments.join(', ')
  if (!result.street) delete result.street
  return result
}

// =====================
// ADDRESS field settings & validation
// =====================

export const AddressFieldSettingsSchema = z.object({
  /** Sub-fields the submitter must fill. */
  requiredParts: z.array(AddressPartEnum).optional(),
  /** ISO alpha-2 code preselected in the country picker. */
  defaultCountry: z.string().length(2).optional(),
  /** Check postal code format for supported countries. */
  validatePostalCode: z.boolean().optional(),
})
export type AddressFieldSettings = z.infer<typeof AddressFieldSettingsSchema>

export const DEFAULT_ADDRESS_REQUIRED_PARTS: AddressPart[] = [
  'street',
  'city',
  'postalCode',
  'country',
]

export function getAddressRequiredParts(
  settings: AddressFieldSettings | null | undefined,
): AddressPart[] {
  const parts = settings?.requiredParts
  return Array.isArray(parts)
    ? parts.filter((p): p is AddressPart =>
        (ADDRESS_PARTS as readonly string[]).includes(p),
      )
    : DEFAULT_ADDRESS_REQUIRED_PARTS
}

/**
 * Validate a submitted ADDRESS value. An optional, fully empty address is
 * valid. Once the address is required or partially filled, every required
 * part must be present, the country must be a known ISO code, and — when
 * enabled — the postal code must match the country's format.
 */
export function validateAddressValue(
  value: unknown,
  options: {
    label: string
    isRequired: boolean
    settings?: AddressFieldSettings | null
  },
): string | null {
  const { label, isRequired, settings } = options
  if (value === undefined || value === null || value === '') {
    return isRequired ? `${label} is required` : null
  }
  if (!isPlainObject(value)) return `${label} must be an address`

  for (const part of ADDRESS_PARTS) {
    const v = value[part]
    if (v === undefined || v === null) continue
    if (typeof v !== 'string') return `${label} must be an address`
    if (v.length > ADDRESS_PART_MAX_LENGTH) {
      return `${label}: ${ADDRESS_PART_LABELS[part]} is too long`
    }
  }

  if (isAddressEmpty(value)) {
    return isRequired ? `${label} is required` : null
  }

  for (const part of getAddressRequiredParts(settings)) {
    const v = value[part]
    if (typeof v !== 'string' || v.trim() === '') {
      return `${label}: ${ADDRESS_PART_LABELS[part]} is required`
    }
  }

  const country = typeof value.country === 'string' ? value.country.trim() : ''
  if (country && !isValidCountryCode(country.toUpperCase())) {
    return `${label}: choose a country from the list`
  }

  const postalCode =
    typeof value.postalCode === 'string' ? value.postalCode.trim() : ''
  if (
    settings?.validatePostalCode &&
    postalCode &&
    country &&
    !isValidPostalCode(postalCode, country)
  ) {
    return `${label}: enter a valid postal code for ${getCountryName(country)}`
  }

  return null
}

// =====================
// Filtering
// =====================

/** Which part an address filter looks at (`any` = whole one-line address). */
export const ADDRESS_FILTER_PARTS = [
  'any',
  'street',
  'city',
  'state',
  'postalCode',
  'country',
] as const
export type AddressFilterPart = (typeof ADDRESS_FILTER_PARTS)[number]

export const ADDRESS_FILTER_PART_LABELS: Record<AddressFilterPart, string> = {
  any: 'Any part',
  street: 'Street',
  city: 'City',
  state: 'State / region',
  postalCode: 'Postal code',
  country: 'Country',
}

export const AddressFilterValueSchema = z.object({
  part: z.enum(ADDRESS_FILTER_PARTS),
  operator: z.enum(['contains', 'equals']),
  text: z.string().max(ADDRESS_PART_MAX_LENGTH),
})
export type AddressFilterValue = z.infer<typeof AddressFilterValueSchema>

export function isAddressFilterValue(
  value: unknown,
): value is AddressFilterValue {
  return AddressFilterValueSchema.safeParse(value).success
}

/**
 * Match a stored address against a sub-field filter. Case-insensitive;
 * `street` also searches the second street line; `country` matches either
 * the ISO code or the country name. An empty filter text matches everything.
 */
export function matchesAddressFilter(
  stored: unknown,
  filter: AddressFilterValue,
): boolean {
  const needle = filter.text.trim().toLowerCase()
  if (!needle) return true
  if (!isPlainObject(stored)) return false
  const address = stored as Address

  const candidates: string[] = (() => {
    switch (filter.part) {
      case 'any':
        return [formatAddressOneLine(address)]
      case 'street':
        return [address.street ?? '', address.street2 ?? '']
      case 'country': {
        const raw = address.country ?? ''
        return [raw, getCountryName(raw)]
      }
      case 'postalCode':
        return [
          address.postalCode ??
            (typeof (stored as Record<string, unknown>).zipCode === 'string'
              ? ((stored as Record<string, unknown>).zipCode as string)
              : ''),
        ]
      default:
        return [address[filter.part] ?? '']
    }
  })()

  return candidates.some(candidate => {
    const text = candidate.trim().toLowerCase()
    if (!text) return false
    return filter.operator === 'equals'
      ? text === needle
      : text.includes(needle)
  })
}
